import { defineMessages } from "react-intl"
import { ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { StatusBadge } from "../../../../common/badge"
import { PREFERENCE_TYPE } from "../../constants"
    
export enum InventoryStockDocumentColumnIds {
    IDENTIFIER = "identifier",
    CREATED_AT_DATE = "createdAt",
    SUPPLIER = "supplier",
    STATUS = "status",
    TOTAL_AMOUNT = "totalAmount",
    ARCHIVED = "isArchived",
    SUPPLIER_PHONE_NUMBER = "supplierPhoneNumber",
    SUPPLIER_TAX_NUMBER = "supplierTaxNumber",
    SUPPLIER_EMAIL = "supplierEmail",
    PRODUCTS_TITLE = "productsTitle",
    PRODUCTS_SKU = "productsSku",
    PRODUCTS_LABELS = "productsLabels",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: InventoryStockDocumentColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/stock-documents/{id}/edit",
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InventoryStockDocumentColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.SUPPLIER,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.STATUS,
        dataType: DataType.COMPONENT,
        dataAttributes: {
            component: StatusBadge,
            props: {
                colorCode: "statusObj.colorCode",
                text: "statusObj.name",
            },
            textAlign: "right",
        },
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InventoryStockDocumentColumnIds.TOTAL_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.totalAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: InventoryStockDocumentColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: InventoryStockDocumentColumnIds.SUPPLIER_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.SUPPLIER_TAX_NUMBER,
        sortId: "contact.taxNumber",
        accessorFn: row => row.contact?.taxNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.SUPPLIER_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: InventoryStockDocumentColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
]

const headerMessages = defineMessages({
    [InventoryStockDocumentColumnIds.IDENTIFIER]: {
        id: "generic-listing.inventory-stock-documents.table-header.identifier",
        defaultMessage: "ID",
    },
    [InventoryStockDocumentColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.inventory-stock-documents.table-header.created-at-date",
        defaultMessage: "Created At Date",
    },
    [InventoryStockDocumentColumnIds.SUPPLIER]: {
        id: "generic-listing.inventory-stock-documents.table-header.supplier",
        defaultMessage: "Supplier",
    },
    [InventoryStockDocumentColumnIds.STATUS]: {
        id: "generic-listing.inventory-stock-documents.table-header.status",
        defaultMessage: "Status",
    },
    [InventoryStockDocumentColumnIds.TOTAL_AMOUNT]: {
        id: "generic-listing.inventory-stock-documents.table-header.total-amount",
        defaultMessage: "Amount",
    },
    [InventoryStockDocumentColumnIds.ARCHIVED]: {
        id: "generic-listing.inventory-stock-documents.table-header.archived",
        defaultMessage: "Archived",
    },
    [InventoryStockDocumentColumnIds.SUPPLIER_PHONE_NUMBER]: {
        id: "generic-listing.inventory-stock-documents.table-header.supplier-phone_number",
        defaultMessage: "Supplier Phone Number",
    },
    [InventoryStockDocumentColumnIds.SUPPLIER_TAX_NUMBER]: {
        id: "generic-listing.inventory-stock-documents.table-header.supplier-tax-number",
        defaultMessage: "Supplier Tax Number",
    },
    [InventoryStockDocumentColumnIds.SUPPLIER_EMAIL]: {
        id: "generic-listing.inventory-stock-documents.table-header.supplier-email",
        defaultMessage: "Supplier Email",
    },
    [InventoryStockDocumentColumnIds.PRODUCTS_TITLE]: {
        id: "generic-listing.inventory-stock-documents.table-header.product-title",
        defaultMessage: "Products Title",
    },
    [InventoryStockDocumentColumnIds.PRODUCTS_SKU]: {
        id: "generic-listing.inventory-stock-documents.table-header.product-sku",
        defaultMessage: "Products SKU",
    },
    [InventoryStockDocumentColumnIds.PRODUCTS_LABELS]: {
        id: "generic-listing.inventory-stock-documents.table-header.product-labels",
        defaultMessage: "Products Labels",
    },
})

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.INVENTORY_STOCK_DOCUMENTS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_STOCK_DOCUMENTS,
    searchPlaceholder: {
        id: "generic-listing.inventory-stock-documents.search-placeholder",
        defaultMessage: "Search stock documents...",
    },
    displayString: {
        id: "generic-listing.inventory-stock-documents.display-string",
        defaultMessage: "Stock Documents",
    },
    searchObjectType: ObjectType.INVENTORY_STOCK_DOCUMENTS,
    bulkActionDefinitions: [],
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/inventory-stock-documents.ts